import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Sparkle, BookOpen, Textbox, Atom, Translate, Headphones,
  Check, X, Spinner, Clock,
} from '@phosphor-icons/react';
import { useLang } from '../contexts/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EX_TYPES = [
  { key: 'vocabulary',  label: { de: 'Vokabeln',     es: 'Vocabulario' }, desc: { de: 'Neue Wörter im Kontext', es: 'Palabras nuevas en contexto' },  icon: Textbox,    color: '#5B8C9A' },
  { key: 'grammar',     label: { de: 'Grammatik',    es: 'Gramática' },   desc: { de: 'Konjugationen und Strukturen', es: 'Conjugaciones y estructuras' }, icon: Atom,       color: '#8B5A3C' },
  { key: 'translation', label: { de: 'Übersetzung',  es: 'Traducción' },  desc: { de: 'DE↔ES Mini-Übungen',      es: 'Mini-ejercicios DE↔ES' },     icon: Translate,  color: '#6D28D9' },
  { key: 'listening',   label: { de: 'Hörverstehen', es: 'Comprensión auditiva' }, desc: { de: 'Übungen mit Audio', es: 'Ejercicios con audio' },   icon: Headphones, color: '#2F6B50' },
  { key: 'reading',     label: { de: 'Leseverstehen', es: 'Comprensión lectora' }, desc: { de: 'Kurze Texte mit Fragen', es: 'Textos cortos con preguntas' }, icon: BookOpen, color: '#E06D53' },
];

/**
 * Generates + evaluates AI-crafted exercises of a chosen type.
 * Re-used by AITeacherHub and by ExercisesPage (for the Hörverstehen generator).
 */
export default function AIExerciseRunner({ user, typeKey, showHeader = true }) {
  const { lang } = useLang();
  const [level, setLevel] = useState(user?.spanish_level || 'A2');
  const [topic, setTopic] = useState('');
  const [generating, setGenerating] = useState(false);
  const [exercise, setExercise] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setGenerating(true); setExercise(null); setAnswer(null); setResult(null);
    try {
      const res = await axios.post(`${API}/exercises/student/ai-generate`,
        { level, exercise_type: typeKey, topic: topic || undefined },
        { withCredentials: true });
      setExercise(res.data);
    } catch { toast.error(lang === 'es' ? 'Error al generar' : 'Fehler beim Generieren'); }
    finally { setGenerating(false); }
  };

  const submit = async () => {
    if (!exercise || answer == null) return;
    try {
      const res = await axios.post(`${API}/exercises/submit`,
        { exercise_id: exercise.exercise_id, answer: exercise.options[answer] },
        { withCredentials: true });
      setResult(res.data);
      if (res.data.is_correct) toast.success(lang === 'es' ? '¡Correcto! +1 punto' : 'Richtig! +1 Punkt');
    } catch { toast.error(lang === 'es' ? 'Error' : 'Fehler'); }
  };

  const typeMeta = EX_TYPES.find(t => t.key === typeKey) || EX_TYPES[0];

  return (
    <div className="space-y-4" data-testid={`ai-ex-runner-${typeKey}`}>
      {showHeader && (
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524]">{typeMeta.label[lang]}</h2>
          <p className="text-sm text-[#57534E] mt-1">{typeMeta.desc[lang]}</p>
        </div>
      )}

      {!exercise && (
        <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 rounded-2xl p-5 border border-amber-200 space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="w-full sm:w-[120px] bg-white" data-testid="ai-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['A1', 'A2', 'B1', 'B2', 'C1'].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <input type="text" value={topic} onChange={e => setTopic(e.target.value)}
              placeholder={lang === 'es' ? 'Tema opcional (p.ej. comida, viaje)' : 'Optionales Thema (z.B. Essen, Reisen)'}
              className="flex-1 bg-white border border-stone-200 rounded-md px-3 py-2 text-sm"
              data-testid="ai-topic" />
            <Button onClick={generate} disabled={generating} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="ai-generate">
              {generating ? <><Spinner size={16} className="animate-spin mr-2" /> {lang === 'es' ? 'Generando…' : 'Wird erstellt…'}</>
                         : <><Sparkle size={16} weight="fill" className="mr-2" /> {lang === 'es' ? 'Generar' : 'Erzeugen'}</>}
            </Button>
          </div>
        </div>
      )}

      {exercise && (
        <div className="bg-white rounded-xl p-5 border border-amber-200 space-y-4" data-testid="ai-ex-active">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className={`level-badge ${exercise.level}`}>{exercise.level}</span>
              <span className="text-xs uppercase tracking-wider text-[#78716C]">{exercise.exercise_type}</span>
              <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <Clock size={11} weight="fill" /> {lang === 'es' ? 'Borrador' : 'In Prüfung'}
              </span>
            </div>
            <button onClick={() => { setExercise(null); setAnswer(null); setResult(null); }} className="text-[#78716C]"><X size={16} /></button>
          </div>
          <h3 className="font-heading text-lg font-semibold text-[#292524]">{exercise.question}</h3>
          {exercise.exercise_type === 'listening' && exercise.audio_base64 && (
            <audio controls preload="metadata" src={`data:audio/mp3;base64,${exercise.audio_base64}`} className="w-full max-w-md h-9 rounded-full" style={{maxHeight:'36px'}} />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {exercise.options?.map((opt, i) => {
              const selected = answer === i;
              const isCorrect = result && result.correct_answer === opt;
              const isWrongPick = result && selected && !result.is_correct;
              return (
                <button key={i} disabled={!!result} onClick={() => setAnswer(i)}
                  className={`p-3 rounded-xl text-left text-sm font-medium border-2 transition-all ${
                    isCorrect ? 'bg-green-100 border-green-400 text-green-800' :
                    isWrongPick ? 'bg-red-100 border-red-400 text-red-800' :
                    selected ? 'border-amber-400 bg-amber-50' :
                    'border-stone-200 bg-white hover:border-amber-300'
                  }`}
                  data-testid={`ai-opt-${i}`}>
                  {opt}
                  {isCorrect && <Check size={14} className="inline ml-2" weight="bold" />}
                  {isWrongPick && <X size={14} className="inline ml-2" weight="bold" />}
                </button>
              );
            })}
          </div>
          {!result ? (
            <Button onClick={submit} disabled={answer == null} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="ai-submit">
              <Check size={16} className="mr-2" weight="bold" /> {lang === 'es' ? 'Corregir' : 'Prüfen'}
            </Button>
          ) : (
            <div className="space-y-2">
              {result.explanation && (
                <p className="text-sm text-[#57534E] bg-stone-50 p-3 rounded-lg border border-stone-200">{result.explanation}</p>
              )}
              <Button onClick={generate} disabled={generating} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="ai-next">
                {generating
                  ? <><Spinner size={16} className="animate-spin mr-2" /> {lang === 'es' ? 'Generando…' : 'Wird erstellt…'}</>
                  : <><Sparkle size={16} className="mr-2" weight="fill" /> {lang === 'es' ? 'Generar otro ejercicio' : 'Weitere Übung erzeugen'}</>}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
